<template>
    <div class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-content wide-modal">
            <button class="close-button" @click="$emit('close')">&times;</button>
            <div class="profile-form">
                <!-- Personal Information Section -->
                <div class="form-section">
                    <h3>Personal Information</h3>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>First Name</label>
                            <input v-model="userData.firstName" type="text" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label>Last Name</label>
                            <input v-model="userData.lastName" type="text" class="form-input" required>
                        </div>
                    </div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Email</label>
                            <input v-model="userData.email" type="email" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label>Preferred Name</label>
                            <input v-model="userData.displayName" type="text" class="form-input">
                        </div>
                    </div>
                </div>

                <!-- Company Information Section -->
                <div class="form-section">
                    <h3>Company Information</h3>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Company Name</label>
                            <input v-model="userData.companyName" type="text" class="form-input">
                        </div>
                        <div class="form-group">
                            <label>Title/Role</label>
                            <input v-model="userData.title" type="text" class="form-input">
                        </div>
                    </div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label>Company Website</label>
                            <input v-model="userData.companyWebsite" type="url" class="form-input" placeholder="https://">
                        </div>
                        <div class="form-group">
                            <label>Area of Business</label>
                            <input v-model="userData.businessArea" type="text" class="form-input">
                        </div>
                    </div>
                </div>

                <div class="form-actions">
                    <button @click="createUser" class="save-btn" :disabled="isSaving">
                        {{ isSaving ? 'Creating...' : 'Create User' }}
                    </button>
                    <p v-if="error" class="error-message">{{ error }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import { userService } from '../services/api';

export default {
    name: 'AddUserModal',
    emits: ['close', 'user-created'],
    setup(props, { emit }) {
        const userData = ref({
            firstName: '',
            lastName: '',
            displayName: '',
            email: '',
            companyName: '',
            title: '',
            companyWebsite: '',
            businessArea: '',
        });
        const isSaving = ref(false);
        const error = ref('');

        const createUser = async () => {
            if (isSaving.value) return;
            
            try {
                isSaving.value = true;
                error.value = '';
                
                // Convert form data to match API expectations
                const apiData = {
                    firstName: userData.value.firstName,
                    lastName: userData.value.lastName,
                    displayName: userData.value.displayName,
                    email: userData.value.email,
                    companyName: userData.value.companyName,
                    title: userData.value.title,
                    companyWebsite: userData.value.companyWebsite,
                    businessArea: userData.value.businessArea
                };
                
                await userService.createUser(apiData);
                emit('user-created');
                emit('close');
            } catch (err) {
                console.error('Error creating user:', {
                    error: err,
                    userData: userData.value,
                    env: process.env.NODE_ENV,
                    apiUrl: process.env.VUE_APP_API_URL
                });
                
                // Handle specific error cases
                if (err.response?.status === 409) {
                    error.value = 'A user with this email address already exists.';
                } else if (err.response?.status === 400) {
                    error.value = err.response?.data?.error || 'Please check all required fields are filled correctly.';
                } else {
                    error.value = 'Failed to create user. Please try again.';
                }
            } finally {
                isSaving.value = false;
            }
        };

        return {
            userData,
            isSaving,
            error,
            createUser
        };
    }
};
</script>

<style scoped>
/* Use the same styles as AdminEditUser.vue */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 20px 30px;
    border-radius: 8px;
    width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
}

.wide-modal {
    margin: 20px auto;
    max-width: 95%;
}

.close-button {
    position: absolute;
    right: 15px;
    top: 15px;
    border: none;
    background: none;
    font-size: 24px;
    cursor: pointer;
    z-index: 1;
}

/* Form Styles */
.form-section {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.form-input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.save-btn {
    background-color: #4285f4;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.save-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.error-message {
    color: #dc3545;
    margin-top: 10px;
}
</style> 